import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { bindOptionSelectorFactory } from '@ff-os/pdp-shared-redux/pluginBindings';
import { PaintTarget } from '@ff-os/rendering-shared/targets';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { createPaintables } from '@ff-scene-plugins/gift-shared/paintables';
import { variablePlugins } from '@ff-scene-plugins/gift-shared/registry';
import { selectActiveVariant } from '../selectors/variantSelectors';
import { GiftPdpState } from '../store';

const adapter = createEntityAdapter<GiftJson.Variable<any>>();
export const adapterSelectors = adapter.getSelectors<GiftPdpState>(s => s.project.variables);

export const { actions, reducer } = createSlice({
    name: 'variables',
    initialState: adapter.getInitialState(),
    reducers: {
        setAll: adapter.setAll,
        upsertMany: adapter.upsertMany
    }
});

export const selectPluginIdByVariableId = createSelector(
    adapterSelectors.selectById,
    v => v?.pluginId);

export const selectNameByVariableId = createSelector(
    adapterSelectors.selectById,
    v => v?.name);

export const selectSizeByVariableId = createSelector(
    adapterSelectors.selectById,
    v => v ? { width: v.width, height: v.height } : undefined);

export const isAiStudioEnabledByVariableId = createSelector(
    adapterSelectors.selectById,
    v => v?.options?.enableAiStudio);

export const selectAiStudioAppIdByVariableId = createSelector(
    adapterSelectors.selectById,
    v => v?.options?.aiStudioStartupAppId);

export const selectAspectRatioByVariableId = createSelector(
    adapterSelectors.selectById,
    v => v ? v.width / v.height : undefined);

export const selectAllVariables = createSelector(
    (state: GiftPdpState) => state.project.variables,
    (variables) => variables.ids
        .map(id => variables.entities[id]!));

export const selectVariableById = createSelector(
    adapterSelectors.selectById,
    v => v);

export const selectNonPinnedVariables = createSelector(
    selectAllVariables,
    (variables) => variables.filter(v => !v.pinned && v.options?.visible !== false));

export const selectHasNonPinnedVariables = createSelector(
    selectNonPinnedVariables,
    (nonPinnedVariables) => nonPinnedVariables.length > 0);

export const selectPinnedVariables = createSelector(
    selectAllVariables,
    (variables) => variables.filter(v => v.pinned && v.options?.visible !== false));

export const selectNonPinnedVariablesWithInputControl = createSelector([
    selectNonPinnedVariables
], (variables) => variables.filter(v => variablePlugins.require(v.pluginId).InputControl));

export const selectHasMultipleNonPinnedVariables = createSelector([
    selectNonPinnedVariablesWithInputControl
], (nonPinnedVariables) => nonPinnedVariables.length > 1);

export const selectHasSingleNonPinnedVariable = createSelector([
    selectNonPinnedVariablesWithInputControl
], (nonPinnedVariables) => nonPinnedVariables.length === 1);

export const selectPinnedVariablesWithInputControl = createSelector([
    selectPinnedVariables
], (variables) => variables.filter(v => variablePlugins.require(v.pluginId).InputControl));

export const selectHasSinglePinnedVariable = createSelector([
    selectPinnedVariablesWithInputControl
], (pinnedVariables) => pinnedVariables.length === 1);

export const selectHasPinnedVariables = createSelector(
    selectPinnedVariables,
    (pinnedVariables) => pinnedVariables.length > 0);

export const selectVariablePaintables = createSelector([
    (state: GiftPdpState) => selectAllVariables(state),
    (state: GiftPdpState) => state.project.inputs.entities,
    (state: GiftPdpState) => selectActiveVariant(state),
    (state: GiftPdpState) => state.auth.jwt,
    (state: GiftPdpState, target: PaintTarget) => target
], createPaintables);

export const createOptionBindings = <TOptions>() => ({
    createOptionSelector: bindOptionSelectorFactory(adapterSelectors).to<TOptions>()
});